import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Users, Calendar, Grid3X3, BadgeCheck, Heart, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamHubPage = () => {
    const { teamId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [team, setTeam] = useState(null);
    const [fixtures, setFixtures] = useState([]);
    const [posts, setPosts] = useState([]);
    const [fans, setFans] = useState([]);
    const [isFollowing, setIsFollowing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('feed');

    const teamName = decodeURIComponent(teamId || '');

    useEffect(() => {
        fetchTeamData();
    }, [teamId]);

    const fetchTeamData = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const response = await axios.get(`${API}/teams/${encodeURIComponent(teamName)}`, { headers });
            
            setTeam(response.data.team);
            setFixtures(response.data.fixtures);
            setPosts(response.data.posts);
            setFans(response.data.fans);
            setIsFollowing(response.data.is_following);
        } catch (error) {
            console.error('Error fetching team data:', error);
            toast.error('Failed to load team data');
        } finally {
            setLoading(false);
        }
    };

    const handleFollow = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API}/teams/${encodeURIComponent(teamName)}/follow`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setIsFollowing(response.data.action === 'followed');
            toast.success(response.data.action === 'followed' ? 'Following team!' : 'Unfollowed team');
        } catch (error) {
            console.error('Error following team:', error);
            toast.error('Failed to follow team');
        }
    };

    const formatFollowers = (count) => {
        if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
        if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
        return count?.toString() || '0';
    };

    const getMatchResult = (fixture) => {
        const isHome = fixture.home_team === teamName;
        const teamScore = isHome ? fixture.home_score : fixture.away_score;
        const opponentScore = isHome ? fixture.away_score : fixture.home_score;
        
        if (fixture.status === 'upcoming') return { text: 'SOON', color: 'text-white/50' };
        if (fixture.status === 'live') return { text: 'LIVE', color: 'text-live' };
        
        if (teamScore > opponentScore) return { text: 'W', color: 'text-green-400' };
        if (teamScore < opponentScore) return { text: 'L', color: 'text-red-400' };
        return { text: 'D', color: 'text-yellow-400' };
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!team) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <p className="text-white/60 mb-4">Team not found</p>
                    <Button onClick={() => navigate(-1)} variant="outline">Go Back</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-black" data-testid="team-hub-page">
            {/* Header with Back Button */}
            <div className="sticky top-0 z-50 glass border-b border-white/10">
                <div className="max-w-md mx-auto flex items-center gap-3 px-4 h-14">
                    <button
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5 text-white" />
                    </button>
                    <h1 className="font-heading text-xl font-bold text-white">
                        Team Hub
                    </h1>
                </div>
            </div>

            {/* Team Header Section */}
            <div className="max-w-md mx-auto px-4 pt-6 pb-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center"
                >
                    {/* Large Team Logo */}
                    <div className="w-28 h-28 rounded-full bg-surface border-4 border-primary/30 flex items-center justify-center overflow-hidden mb-4 shadow-lg shadow-primary/20">
                        <img 
                            src={team.logo} 
                            alt={team.name}
                            className="w-24 h-24 object-contain"
                            onError={(e) => {
                                e.target.src = `https://ui-avatars.com/api/?name=${team.name}&background=169239&color=fff&size=128`;
                            }}
                        />
                    </div>

                    {/* Team Name */}
                    <h2 className="text-white text-2xl font-heading font-bold mb-2">
                        {team.name}
                    </h2>

                    {/* League Badge */}
                    <span className="text-xs bg-white/10 text-white/70 px-3 py-1 rounded-full mb-4">
                        {team.league}
                    </span>

                    {/* Follow Button */}
                    <Button
                        onClick={handleFollow}
                        variant={isFollowing ? "outline" : "default"}
                        className={`mb-4 rounded-full px-8 ${
                            isFollowing 
                                ? 'border-primary text-primary hover:bg-primary/10' 
                                : 'bg-primary hover:bg-primary-hover text-white'
                        }`}
                        data-testid="follow-button"
                    >
                        {isFollowing ? 'Following' : 'Follow'}
                    </Button>

                    {/* Stats Row */}
                    <div className="flex items-center gap-8">
                        <div className="text-center">
                            <p className="font-heading text-2xl font-bold text-white">
                                {formatFollowers(team.followers_count)}
                            </p>
                            <p className="text-white/60 text-xs uppercase">Followers</p>
                        </div>
                        <div className="w-px h-8 bg-border" />
                        <div className="text-center">
                            <p className="font-heading text-2xl font-bold text-primary">
                                #{team.ranking}
                            </p>
                            <p className="text-white/60 text-xs uppercase">Ranking</p>
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Tabs Section */}
            <div className="max-w-md mx-auto px-4 pb-24">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="w-full bg-surface border border-border mb-4">
                        <TabsTrigger 
                            value="feed" 
                            className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                            data-testid="tab-feed"
                        >
                            <Grid3X3 className="w-4 h-4 mr-2" />
                            Feed
                        </TabsTrigger>
                        <TabsTrigger 
                            value="fixtures" 
                            className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                            data-testid="tab-fixtures"
                        >
                            <Calendar className="w-4 h-4 mr-2" />
                            Fixtures
                        </TabsTrigger>
                        <TabsTrigger 
                            value="fans" 
                            className="flex-1 data-[state=active]:bg-primary data-[state=active]:text-white"
                            data-testid="tab-fans"
                        >
                            <Users className="w-4 h-4 mr-2" />
                            Fans
                        </TabsTrigger>
                    </TabsList>

                    {/* Feed Tab */}
                    <TabsContent value="feed">
                        {posts.length === 0 ? (
                            <div className="py-12 text-center">
                                <Grid3X3 className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                <p className="text-white/60">No posts yet</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-3 gap-1" data-testid="team-posts-grid">
                                {posts.map((post, index) => (
                                    <motion.div
                                        key={post.id}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="aspect-square bg-surface rounded overflow-hidden relative group cursor-pointer"
                                        onClick={() => navigate('/')}
                                    >
                                        <img
                                            src={post.media_url}
                                            alt={post.caption}
                                            className="w-full h-full object-cover group-hover:opacity-80 transition-opacity"
                                        />
                                        {/* Overlay on hover */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                                            <div className="flex items-center gap-1 text-white text-sm">
                                                <Heart className="w-4 h-4" />
                                                {post.likes_count}
                                            </div>
                                            <div className="flex items-center gap-1 text-white text-sm">
                                                <MessageCircle className="w-4 h-4" />
                                                {post.comments_count}
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Fixtures Tab */}
                    <TabsContent value="fixtures">
                        {fixtures.length === 0 ? (
                            <div className="py-12 text-center">
                                <Calendar className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                <p className="text-white/60">No fixtures found</p>
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="team-fixtures-list">
                                {fixtures.map((fixture, index) => {
                                    const result = getMatchResult(fixture);
                                    const isHome = fixture.home_team === teamName;
                                    const opponent = isHome ? fixture.away_team : fixture.home_team;
                                    const opponentLogo = isHome ? fixture.away_logo : fixture.home_logo;
                                    
                                    return (
                                        <motion.div
                                            key={fixture.id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="bg-card-bg border border-border rounded-xl p-4"
                                            data-testid={`fixture-${fixture.id}`}
                                        >
                                            <div className="flex items-center justify-between">
                                                {/* Result Badge */}
                                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${
                                                    fixture.status === 'live' ? 'bg-live/20' : 'bg-surface'
                                                }`}>
                                                    <span className={`text-sm ${result.color}`}>
                                                        {result.text}
                                                    </span>
                                                </div>

                                                {/* Match Info */}
                                                <div className="flex-1 px-4">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="text-white/50 text-xs">
                                                            {isHome ? 'vs' : '@'}
                                                        </span>
                                                        <img 
                                                            src={opponentLogo}
                                                            alt={opponent}
                                                            className="w-5 h-5 rounded-full"
                                                        />
                                                        <span className="text-white font-medium text-sm">
                                                            {opponent}
                                                        </span>
                                                    </div>
                                                    <p className="text-white/50 text-xs">{fixture.league}</p>
                                                </div>

                                                {/* Score */}
                                                <div className="text-right">
                                                    {fixture.status !== 'upcoming' ? (
                                                        <p className="font-heading text-xl font-bold text-white">
                                                            {fixture.home_score} - {fixture.away_score}
                                                        </p>
                                                    ) : (
                                                        <p className="text-white/50 text-sm">TBD</p>
                                                    )}
                                                    {fixture.status === 'live' && (
                                                        <p className="text-live text-xs font-medium">
                                                            {fixture.minute}'
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    {/* Fans Tab */}
                    <TabsContent value="fans">
                        {fans.length === 0 ? (
                            <div className="py-12 text-center">
                                <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                                <p className="text-white/60">No fans yet</p>
                                <p className="text-white/40 text-sm mt-1">Be the first to follow!</p>
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="team-fans-list">
                                {fans.map((fan, index) => (
                                    <motion.div
                                        key={fan.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.03 }}
                                        className="flex items-center gap-3 bg-surface border border-border rounded-xl p-3 cursor-pointer hover:bg-white/5 transition-colors"
                                        onClick={() => navigate(`/profile/${fan.id}`)}
                                        data-testid={`fan-${fan.id}`}
                                    >
                                        {/* Avatar */}
                                        <Avatar className="w-10 h-10 border border-border">
                                            <AvatarImage src={fan.avatar} alt={fan.username} />
                                            <AvatarFallback className="bg-primary text-white">
                                                {fan.username?.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>

                                        {/* User Info */}
                                        <div className="flex-1">
                                            <div className="flex items-center gap-1">
                                                <span className="text-white font-medium text-sm">{fan.username}</span>
                                                {fan.is_verified && (
                                                    <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                                                )}
                                            </div>
                                            {fan.role && (
                                                <span className="text-white/50 text-xs capitalize">{fan.role}</span>
                                            )}
                                        </div>

                                        {/* Followers Count */}
                                        <div className="text-right">
                                            <p className="text-white/60 text-xs">
                                                {formatFollowers(fan.followers_count)} followers
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default TeamHubPage;
